import { FertiliserLiquidUnit } from "@omnia/shared/generated/enums"
import { checkApplicationDate } from "../../../nvz/applicationDate"
import { arrayMax, isNumber, isUndefined, param, selectMany, sum } from "../../../omnia"
import { getAppRounding, getTargetNutrientValueFromAnalysis, INutritionCalcService, normalizeAnalysis } from "../../../services/calcService"
import { getManureTypeApp } from "../../../services/manureAnalysis"
import { getProductUnitsTextNormalized, getRoundedValueForUnit } from "../../../services/units"
import { StockVm } from "../../../stocks/stockVm"
import { FertiliserAppUnit, IFertiliserAnalysis, INutritionAppQty, INutritionPlanCroppingApp, INutritionPlanProductApp, INutritionProductApp, INutritionService, ManureAppMethod, NutrientType, ProductAppInput } from "../../../types"
import { LimeEnabledFromYear } from "../../../utils"
import { CroppingEditVm } from "../cropping/croppingEditVm"
import { IPlanEditContext, IStockNutrientModel } from "../types"
import { AutocompleteService } from "./autocomplete"
import { CroppingOverride } from "./croppingOverride"
import { calculateAmountInStock, useAllStock } from "./stock"
import { getCanSyncAllInputs, syncRates } from "./syncRates"
import { updateUnits } from "./units"

import { isDate, isDefined } from "@omnia/shared/utils/utils"
import { formatChemicalCompoundNameAsHtml } from "@omnia/shared/utils/format"
import { getNutrientColorProvider } from "@omnia/shared/nutrition/colors"
import { colorToCss } from "@omnia/shared/color/color"

const manureAppMethods = param.ManureAppMethod()

export class ProductApplicationVm implements INutritionProductApp {

    public readonly editId: number

    public readonly initProductApp: INutritionPlanProductApp
    public readonly initProductAmount: number

    private _app: INutritionAppQty;

    private calcSvc: INutritionCalcService

    private _stock: StockVm;
    private _newStock: StockVm;

    private _analysis: IFertiliserAnalysis;

    private _overrides: { [key: number]: CroppingOverride };

    public remove: boolean;

    public nutrients: IStockNutrientModel[];
    public nutrientsMaxRate: number;

    private _targetNutrient: NutrientType;

    private _calculatedMaxProductRate: number

    public justAdded: boolean = false;
    public showActualData: boolean = false;
    public dateInvalid: boolean = false;


    constructor(public ctxt: IPlanEditContext, public svc: INutritionService, public readonly productApp: INutritionPlanProductApp) {

        this.editId = ctxt.getNextAppEditId()

        this.initProductApp = productApp
        this.initProductAmount = productApp
            ? ctxt.initialAppTotals.find(t => t.appId === productApp.NutritionPlanProductAppId)?.amount
            : undefined

        this.calcSvc = svc.calculationService;

        this.productApp = angular.copy(productApp);
        this._app = this.productApp;

        this._overrides = this.setupOverrides();

        this.targetNutrient = productApp.TargetNutrient;

        ctxt.applications
    }

    clone(clearOverrides: boolean = false): ProductApplicationVm {

        var vm = new ProductApplicationVm(this.ctxt, this.svc, this.productApp);
        vm.stock = this.stock;

        if (!clearOverrides) {
            var ovr = this._overrides;
            vm._overrides = {};

            Object.keys(ovr).forEach(s => {
                vm._overrides[s] = (<CroppingOverride>ovr[s]).clone(vm);
            });
        } else {
            vm._overrides = {};
            vm.productApp.Cropping = [];
        }

        return vm;
    }

    public resetRates() {

        this._app.NutrientPct = undefined;
        this._app.NutrientRate = undefined;
        this._app.ProductRate = undefined;
        this._overrides = {};
        this.productApp.Cropping = [];
        this.updateOverridesOnModel();

    }

    private setupOverrides(): { [key: number]: CroppingOverride } {

        var ovr: { [key: number]: CroppingOverride } = {};

        var cropping = this.productApp.Cropping;

        if (cropping) {

            cropping.forEach(c => {
                ovr[c.CroppingId] = new CroppingOverride(this, c.CroppingId, c);
            });

        }

        return ovr;

    }

    setOverride(ovr: CroppingOverride) {
        this._overrides[ovr.croppingId] = ovr;
    }

    overrideCroppingRate(cropping: CroppingEditVm, value: { rate: number, type: ProductAppInput }) {

        const croppingId = cropping.croppingId

        if (!value) {

            delete this._overrides[croppingId]

        }
        else {

            const productNutrientAnalysis = getTargetNutrientValueFromAnalysis(this.targetNutrient, this.analysis)

            const { type, rate } = value

            const ovr = this._overrides[croppingId] || new CroppingOverride(this, croppingId)

            ovr.LastInputUsed = type
            ovr.NutrientPct = undefined
            ovr.ProductRate = undefined
            ovr.NutrientRate = undefined

            this._overrides[croppingId] = ovr

            switch (type) {

                case ProductAppInput.ProductRate:
                    ovr.ProductRate = rate
                    ovr.NutrientRate = this.roundToProductUnit(rate * productNutrientAnalysis)
                    break;

                case ProductAppInput.NutrientRate:
                    ovr.NutrientRate = rate
                    ovr.ProductRate = this.roundToProductUnit(productNutrientAnalysis > 0 ? rate / productNutrientAnalysis : 0)
                    break;

            }

        }

        this.updateOverridesOnModel()

    }

    get readOnly() {
        return this.ctxt?.readOnly
    }

    get targetNutrient() {
        return this._targetNutrient
    }

    get targetNutrientKey() {
        return NutrientType[this._targetNutrient]
    }

    set targetNutrient(value) {
        this._targetNutrient = value
        this.updateUnits()
        this.update()
        this.syncRates()
    }

    get lastInputUsed(): ProductAppInput {
        return this._app.LastInputUsed;
    }

    set lastInputUsed(value: ProductAppInput) {
        this._app.LastInputUsed = value;
    }

    get useBiomass() {
        return this.ctxt?.useBiomass
    }

    updateTargetModel(model: INutritionPlanProductApp, api: boolean) {

        const app = this.productApp

        model._EditId = api ? undefined : this.editId
        model.NutritionPlanProductAppId = app.NutritionPlanProductAppId
        model.TargetNutrient = this.targetNutrient
        model.LastInputUsed = app.LastInputUsed
        model.AutoComplete = this.autoComplete
        model.ProductAmount = this.calculateTotalAmount().totalAmount

        model.Cropping = [...(app.Cropping || [])]

        const cropping = (this.ctxt.getSelectedCropping() || []).map(c => c.croppingId)

        if (model.Cropping) {
            model.Cropping = model.Cropping.filter(c => cropping.indexOf(c.CroppingId) !== -1)
        }

        var s = this.stock;

        if (s) {
            s.updateProductApp(model, !api);
        }

    }

    isValid(): boolean {

        if (this.remove) return true;
        if (!this.hasProduct()) return false;
        if (!this.targetNutrient) return false;
        if (!this._newStock) return false;

        let app = this.productApp;

        if (!isDate(app.ApplicationDate)) {
            return false;
        }

        const cropping = this.ctxt.getSelectedCropping(true)

        this.dateInvalid = !checkApplicationDate(app.ApplicationDate, cropping, this.svc);

        if (this.dateInvalid) {
            return false;
        }

        if (!this.useBiomass) {
            if ((!isNumber(this.productRate)) && (!isNumber(this.nutrientPct)) && (!isNumber(this.nutrientRate))) {
                return false;
            }
        }

        if (this.isManure() && (!app.Incorporation)) {
            return false;
        }

        if (!this.hasActualData()) {
            return true;
        }

        return this.hasValidActualData();
    }

    /**
     * Returns true if associated with a stock
     */
    hasProduct(): boolean {
        return !!this._stock;
    }

    /**
     * Returns true if associated with a stock, and that stock is not empty
     */
    hasStock(): boolean {
        return this._stock && (this._stock.getAmountInStock() > 0);
    }

    /**
     * Returns the amount of product actually avaiable in the selected stock
     */
    getAmountInStock(): number {
        return calculateAmountInStock(this.ctxt, this._stock, this.svc)
    }

    hasSpecificGravity(): boolean {
        const { _analysis: a } = this
        return a?.Liquid && (a.LiquidUnit == FertiliserLiquidUnit.WeightForWeight);
    }

    getUnitsText(perHectare: boolean = true) {
        const text = getProductUnitsTextNormalized(this)
        return perHectare
            ? text + "/ha"
            : text
    }

    getNutrientUnitsText() {
        const text = getProductUnitsTextNormalized(this, true)
        return text + "/ha"
    }

    isNew(): boolean {
        return isUndefined(this.productApp.NutritionPlanProductAppId) || this.productApp.NutritionPlanProductAppId === 0;
    }

    readonly canSyncAllInputs = getCanSyncAllInputs(this)

    public syncRates(reset: boolean = false) {

        if (!this.analysis) {
            return
        }

        return syncRates(this, reset)

    }

    public getNutrientRateForProductRate(productRate: number): number {

        let nutrientPct = getTargetNutrientValueFromAnalysis(this.targetNutrient, this.analysis);

        return this.roundToProductUnit(productRate * nutrientPct);

    }

    public getProductRateForNutrientRate(nutrientRate: number, analysis?: IFertiliserAnalysis): number {

        let nutrientPct = getTargetNutrientValueFromAnalysis(this.targetNutrient, analysis || this.analysis);

        return this.roundToProductUnit(nutrientPct > 0 ? nutrientRate / nutrientPct : 0);

    }

    doAutoComplete(): void {
        this.productApp.AutoComplete = true;
        AutocompleteService.Autocomplete(this.svc, this);
    }

    useAllStock() {
        return useAllStock(this.ctxt, this.calcSvc, this)
    }

    get description() {
        return this._stock?.description
    }

    get stock(): StockVm {
        return this._stock
    }

    set stock(value: StockVm) {

        if (this._stock != value) {

            const init = !this._stock

            this._stock = value
            this._newStock = value

            this.updateAnalysis()
            this.updateUnits()
            this.updateStockNutrients()

            if (!init) {
                this.resetRates()
                this.resetRateLimits()
            }

            this.update()

        }

    }

    get newStock(): StockVm {
        return this._newStock;
    }

    set newStock(value: StockVm) {
        this._newStock = value;
        if (!!value) {
            this.stock = value
            this.selectTargetNutrient()
            this.selectIncorporationMethod()
            this.update()
        }
    }

    get analysis(): IFertiliserAnalysis {
        return this._analysis;
    }

    get productAmount(): number {
        this.updateTargetModel(this.productApp, false)
        return this.calculateTotalAmount().totalAmount
    }

    get displayedAmount() {
        return this.ctxt.useBiomass
            ? this.initProductAmount
            : this.productAmount
    }

    private selectIncorporationMethod() {

        const { stock } = this

        if (stock && stock.isManure()) {

            const type = stock.manureType

            if (type) {

                let methods = manureAppMethods

                if (type.Applications && type.Applications.length > 0) {
                    const region = this.svc.holding.Region
                    methods = manureAppMethods.filter(p => getManureTypeApp(type, p.value, region))
                }

                if (methods.length > 0) {
                    this.productApp.Incorporation = methods[0].value;
                    this.update()
                }
            }

        }

    }

    calculateTotalAmount() {

        const rounding = getAppRounding(this.productApp)

        const cropping = this.ctxt.getSelectedCropping()

        const amounts = cropping.map(c => this.calcSvc.calculateProductAmount(c.planRequirements, this.productApp, c.cropping))

        let totalAmount = sum(amounts, a => a.amount);
        totalAmount = Math.round(totalAmount * rounding) / rounding

        const totalArea = sum(amounts, a => a.area)

        return { totalAmount, totalArea, rounding }


    }

    calculateAverageNutrientRate() {

        const cropping = this.ctxt.getSelectedCropping()

        const rates = cropping
            .map(c => {

                const { amount, area } = this.calcSvc.calculateProductAmount(c.planRequirements, this.productApp, c.cropping)

                if (area) {

                    const productRate = amount / area

                    const nutrientRate = this.calcSvc.calculateNutrientRateFromProductRate(productRate, this.productApp, this.targetNutrient, c.cropping)

                    return {
                        rate: nutrientRate,
                        total: nutrientRate * area,
                        area
                    }

                }

            })
            .filter(a => !!a)

        let area = sum(rates, a => a.area)

        if (area) {

            let total = sum(rates, a => a.total)

            const rounding = getAppRounding(this.productApp)

            total /= area
            total = Math.round(total * rounding) / rounding

            return total

        }

    }

    calculateAverageProductRate() {

        const { totalAmount, totalArea, rounding } = this.calculateTotalAmount()

        if (totalArea) {
            const avg = totalAmount / totalArea
            const r = Math.round(avg * rounding) / rounding
            return r
        }

    }

    isLiquid(): boolean {
        return this.stock && this.stock.isLiquid();
    }

    isSolid(): boolean {
        return !this.isLiquid();
    }

    private selectTargetNutrient() {

        const analysis = this.analysis;

        if (analysis) {

            const nutrients = param.NutrientType()

            if (analysis.DefaultTargetNutrient) {
                this.targetNutrient = analysis.DefaultTargetNutrient
                return
            }

            let enableLime = this.svc.yearId >= LimeEnabledFromYear;

            let values = nutrients.map(p => ({ param: p, value: analysis[p.text] }))
                .filter(p => enableLime || (p.param.value != NutrientType.Lime))
                .filter(i => (i.value || 0) > 0);

            values = values.sort((v1, v2) => v2.value - v1.value);

            this.targetNutrient = values[0].param.value;

        }

    }

    private updateStockNutrients() {

        var s = this._stock;
        if (!s) {
            this.nutrients = null;
            return;
        }

        let nutrients = ["N", "SO3", "P2O5", "K2O", "MgO"].map(n => this.getNutrientModel(s, n));

        if (this.svc.yearId >= LimeEnabledFromYear) {
            nutrients.push(this.getLimeModel(s));
        }

        this.nutrients = nutrients.filter(n => !!n);

        var values = this.nutrients.map(r => r.value);

        values = values.filter(v => isNumber(v)).map(v => v / 100);

        this.nutrientsMaxRate = Math.max.apply(Math, values);

    }

    private getNutrientModel(stock: StockVm, key: string): IStockNutrientModel {

        if (!(key && stock)) return null;

        let value = stock.getNutrientValue(key);
        if (!value) return null;

        let pct: number;

        if (isNumber(value)) {
            pct = Math.round(value * 1000) / 10;
        }

        return {
            key: key,
            htmlText: formatChemicalCompoundNameAsHtml(key),
            value: pct,
            valueText: isDefined(pct) ? pct + "&nbsp;%" : "-",
            id: stock.nutrients.find(n => n.key === key).id
        }

    }

    private getLimeModel(stock: StockVm): IStockNutrientModel {

        if (!stock) return null;

        let value = stock.analysis.NeutralisingValue;
        if (!value) return null;

        let color = getNutrientColorProvider("Lime").getReferenceColor();

        return {
            key: "Lime",
            htmlText: "NV",
            value: value,
            valueText: value.toString(),
            id: NutrientType.Lime,
            style: { 'background-color': colorToCss(color) }
        };

    }

    /**
     *
     * Applications Methods
     *
     */

    get nutrientPct(): number {
        return this._app.NutrientPct;
    }

    set nutrientPct(value: number) {
        this._app.NutrientPct = value;
        if (value) {
            this.lastInputUsed = ProductAppInput.NutrientPct;
            this.syncRates();
        }
        this.update()
    }

    get nutrientRate(): number {
        return this._app.NutrientRate;
    }

    set nutrientRate(value: number) {
        this._app.NutrientRate = value;
        if (value) {
            this.lastInputUsed = ProductAppInput.NutrientRate;
            this.syncRates();
        } else if (!value && this.lastInputUsed === ProductAppInput.NutrientRate) {
            this._app.ProductRate = value;
        }
        this.update()
    }

    get productRate(): number {
        return this._app.ProductRate;
    }

    set productRate(value: number) {

        this._app.ProductRate = value;

        if (value) {
            this.lastInputUsed = ProductAppInput.ProductRate;
            this.syncRates()
            this.resetRateLimits()
        } else if (!value && this.lastInputUsed === ProductAppInput.ProductRate) {
            this._app.NutrientRate = value;
        }

        this.update()
    }


    private updateAnalysis() {

        const { stock: stockVm, calcSvc } = this

        const stock = stockVm?.actualStock

        if (stock) {
            this._analysis = calcSvc.getAnalysis(stock)
            return
        }

        const { analysis } = stockVm

        this._analysis = analysis
            ? normalizeAnalysis(analysis)
            : undefined

    }

    get applicationDate(): Date {
        return this.productApp.ApplicationDate;
    }

    set applicationDate(value: Date) {
        this.productApp.ApplicationDate = value;
        this.update()
    }

    get incorporation(): ManureAppMethod {
        return this.productApp.Incorporation;
    }

    set incorporation(value: ManureAppMethod) {
        this.productApp.Incorporation = value;
        this.update()
    }

    isManure() {
        return this.stock && this.stock.isManure()
    }

    isLime() {
        return this.targetNutrient == NutrientType.Lime
    }

    showTonnes() {
        return this.isManure()
    }

    get referenceDate(): Date {
        return this.productApp.ActualDate || this.productApp.ApplicationDate;
    }

    hasActualData(): boolean {
        return !!this.productApp.ActualDate || !!this.productApp.ActualRate;
    }

    hasValidApplicationDate(): boolean {
        return isDate(this.productApp.ApplicationDate);
    }

    hasValidActualData(): boolean {
        return isDate(this.productApp.ActualDate) && isNumber(this.productApp.ActualRate);
    }

    get hasNutrientRate(): boolean {
        return this.nutrientRate && isNumber(this.nutrientRate);
    }

    get hasNutrientPct(): boolean {
        return this.nutrientPct && isNumber(this.nutrientPct);
    }

    get hasProductRate(): boolean {
        return this.productRate && isNumber(this.productRate);
    }

    get canSetNutrientRate() {
        return (this.hasValidApplicationDate() && (this.canSyncAllInputs() || (!this.hasNutrientPct))) && (!this.useBiomass) && (!this.readOnly)
    }

    get canSetNutrientPct() {
        return this.hasValidApplicationDate() && (this.canSyncAllInputs() || (!this.hasNutrientRate && !this.hasProductRate)) && (!this.useBiomass) && (!this.readOnly)
    }

    get canSetProductRate() {
        return this.hasValidApplicationDate() && (this.canSyncAllInputs() || (!this.hasNutrientPct)) && (!this.useBiomass) && (!this.readOnly)
    }

    get autoComplete(): boolean {
        return this.productApp.AutoComplete;
    }

    get usesFlatProductRate() {
        return this.hasProductRate && (this.lastInputUsed == ProductAppInput.ProductRate)
    }

    get calculatedMaxRate() {
        return this._calculatedMaxProductRate
    }

    get minRate() {
        return this.productApp.MinProductRate
    }

    set minRate(value) {
        this.productApp.MinProductRate = value
        this.update()
    }

    get maxRate() {
        return this.productApp.MaxProductRate
    }

    set maxRate(value) {
        this.productApp.MaxProductRate = value
        this.update()
    }

    get cutOffRate() {
        return this.productApp.CutOffProductRate
    }

    set cutOffRate(value) {
        this.productApp.CutOffProductRate = value
        this.update()
    }

    getCroppingOverride(croppingId: number): CroppingOverride {
        return this._overrides[croppingId];
    }

    /**
     * Returns true if this application has overrides for the cropping identified by @croppingId,
     * or for any cropping if @croppingId is not set
     *
     * @param croppingId
     */
    hasOverrides(croppingId?: number): boolean {

        if (isNumber(croppingId)) {
            return this.checkActualOverride(this.getCroppingOverride(croppingId));
        }

        return Object.keys(this._overrides).some(id => {
            return this.checkActualOverride(this._overrides[id]);
        });

    }

    private checkActualOverride(ovr: CroppingOverride): boolean {
        return ovr && (!ovr.compareTo(this.productApp));
    }

    public updateOverridesOnModel() {

        const cropping = Object.keys(this._overrides).map(k => {

            const ovr = this._overrides[k];

            return <INutritionPlanCroppingApp>{

                NutritionPlanProductAppId: this.productApp.NutritionPlanProductAppId,

                CroppingId: ovr.croppingId,

                NutrientPct: ovr.NutrientPct,
                NutrientRate: ovr.NutrientRate,
                ProductRate: ovr.ProductRate,

                LastInputUsed: ovr.LastInputUsed

            };

        });

        this.productApp.Cropping = cropping;

    }

    private updateUnits() {
        updateUnits(this.productApp, this.stock, this._targetNutrient)
    }

    roundToProductUnit(value: number): number {
        const v = getRoundedValueForUnit(value, this.productApp.ProductUnit)
        return isNaN(v) ? undefined : v
    }

    private update() {
        this.ctxt.updateTargetModel(this)
    }

    resetRateLimits() {

        const app = this.productApp

        if (app) {
            app.MinProductRate = null
            app.MaxProductRate = null
            app.CutOffProductRate = null
        }

    }

    recalculateMaxProductRate() {

        const cropping = this.ctxt.getSelectedCropping()

        const app = this.productApp

        const reqs = selectMany(
            cropping
                .filter(c => c.planRequirements)
                .map(c => [c.planRequirements.crop].concat(c.planRequirements.zones || []).map(r => ({ cropping: c.cropping, req: r })))
        )

        const rates = reqs
            .map(c => this.calcSvc.calculateProductRate(c.req, app, c.cropping, false))
            .concat([app.ProductRate])
            .filter(r => r > 0)

        let maxRate = arrayMax(rates, r => r)

        switch (this.productApp.ProductUnit) {
            case FertiliserAppUnit.M3:
            case FertiliserAppUnit.T:
                maxRate = Math.ceil(maxRate * 20) / 20
                break;

            default:
                maxRate = Math.ceil(maxRate / 10) * 10
                break;

        }

        this._calculatedMaxProductRate = maxRate

    }

}
